<template>
  <div class="mb-4">
    <div class="relative">
      <label
        v-if="label != ''"
        class="block mb-2 text-sm font-medium text-textBlack1 dark:text-textWhite1 transition-colors duration-200"
      >
        {{ label }}
        <span v-if="required" class="text-red-500 ml-1">*</span>
      </label>

      <div class="relative">
        <textarea
          :id="id"
          :rows="rows"
          :value="modelValue"
          @input="checkInput"
          @focus="handleFocus"
          @blur="handleBlur"
          v-bind="$attrs"
          :placeholder="placeholder"
          :class="textareaClasses"
          :disabled="disabled"
          :readonly="!isEditable"
          :maxlength="maxLength"
        ></textarea>
        
        <!-- Character count -->
        <div v-if="maxLength" class="absolute bottom-2 right-2 text-xs text-gray-400 dark:text-gray-500">
          {{ modelValue?.length || 0 }}/{{ maxLength }}
        </div>
      </div>

      <!-- Error message -->
      <div v-if="isError && errorMsg" class="error-message mt-2 flex items-center text-red-500 text-sm">
        <i class="fa fa-exclamation-circle mr-2"></i>
        {{ errorMsg }}
      </div>
      
      <!-- Helper text -->
      <div v-if="helperText && !isError" class="helper-text mt-2 text-gray-500 dark:text-gray-400 text-sm">
        {{ helperText }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed } from "vue";

const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  modelValue: {
    type: String,
    default: "",
  },
  isError: {
    type: Boolean,
    default: false,
  },
  errorMsg: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    default: "",
  },
  rows: {
    type: Number,
    default: 3,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isEditable: {
    type: Boolean,
    default: true,
  },
  required: {
    type: Boolean,
    default: false,
  },
  helperText: {
    type: String,
    default: "",
  },
  maxLength: {
    type: Number,
    default: null,
  },
  size: {
    type: String,
    default: "md",
    validator: (value) => ["sm", "md", "lg"].includes(value),
  },
  resize: {
    type: String,
    default: "vertical",
    validator: (value) => ["none", "both", "horizontal", "vertical"].includes(value),
  },
});

const emit = defineEmits(["update:isError", "update:modelValue", "focus", "blur"]);

const isFocused = ref(false);

// Size classes
const sizeClasses = computed(() => {
  const sizes = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-2.5 text-sm",
    lg: "px-4 py-3 text-base",
  };
  return sizes[props.size];
});

// Resize classes
const resizeClasses = computed(() => {
  const resizeOptions = {
    none: "resize-none",
    both: "resize",
    horizontal: "resize-x",
    vertical: "resize-y",
  };
  return resizeOptions[props.resize];
});

// Compute textarea classes based on props and state
const textareaClasses = computed(() => {
  const baseClasses = [
    "block w-full rounded-lg transition-all duration-200 ease-in-out",
    "focus:outline-none focus:ring-2 focus:ring-opacity-50",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    "read-only:bg-gray-50 dark:read-only:bg-gray-800 read-only:cursor-default",
    sizeClasses.value,
    resizeClasses.value,
  ];

  // Error state
  if (props.isError) {
    baseClasses.push(
      "border-red-500 text-red-500 focus:ring-red-500",
      "dark:border-red-600 dark:text-red-500"
    );
  } else {
    // Normal state
    baseClasses.push(
      "bg-white dark:bg-bgFullDark border border-gray-300 dark:border-gray-600",
      "text-textBlack1 dark:text-white",
      "placeholder-gray-400 dark:placeholder-gray-200",
      "focus:ring-blue-500 focus:border-blue-500"
    );
  }

  // Focus state
  if (isFocused.value && !props.isError) {
    baseClasses.push("ring-2 ring-blue-500 ring-opacity-20");
  }

  // Disabled state
  if (props.disabled) {
    baseClasses.push("bg-gray-100 dark:bg-gray-800 cursor-not-allowed");
  }

  return baseClasses.join(" ");
});

// Event handlers
const checkInput = (e) => {
  emit("update:modelValue", e.target.value);
};

const handleFocus = (e) => {
  isFocused.value = true;
  emit("focus", e);
};

const handleBlur = (e) => {
  isFocused.value = false;
  emit("blur", e);
};
</script>

<style scoped>
/* Custom focus ring animation */
textarea:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Smooth transitions for all states */
textarea {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Error state animation */
textarea.error {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}

/* Custom scrollbar */
textarea::-webkit-scrollbar {
  width: 6px;
}

textarea::-webkit-scrollbar-track {
  background: transparent;
}

textarea::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.3);
  border-radius: 3px;
}

textarea::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.5);
}

/* Dark mode scrollbar */
.dark textarea::-webkit-scrollbar-thumb {
  background: rgba(75, 85, 99, 0.3);
}

.dark textarea::-webkit-scrollbar-thumb:hover {
  background: rgba(75, 85, 99, 0.5);
}

/* Placeholder styling */
textarea::placeholder {
  transition: color 0.2s ease-in-out;
}

textarea:focus::placeholder {
  color: rgba(156, 163, 175, 0.7);
}

/* Error message animation */
.error-message {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Helper text styling */
.helper-text {
  transition: all 0.2s ease-in-out;
}

/* Disabled state styling */
textarea:disabled {
  background-color: rgba(243, 244, 246, 0.5);
  color: rgba(107, 114, 128, 0.7);
  cursor: not-allowed;
}

.dark textarea:disabled {
  background-color: rgba(31, 41, 55, 0.5);
  color: rgba(156, 163, 175, 0.7);
}

/* Read-only state styling */
textarea[readonly] {
  background-color: rgba(249, 250, 251, 0.8);
  cursor: default;
}

.dark textarea[readonly] {
  background-color: rgba(31, 41, 55, 0.8);
}

/* Character count styling */
.character-count {
  transition: all 0.2s ease-in-out;
}

/* Auto-resize functionality */
textarea {
  min-height: 2.5rem;
  max-height: 20rem;
  overflow-y: auto;
}
</style>
